/**
 * One SIC code, as every list on the catalogue renders it.
 *
 * Search results, category contents, the classification tree and the shortlist
 * all show the same thing - code, description, where it sits, what state it is
 * in - so they share one row rather than four that drift apart.
 *
 * Two things are deliberate:
 *   - the description is always shown next to the code, never a bare number;
 *   - when a search produced the row, *why it matched* is shown underneath, so
 *     a stray hit is visible as a stray hit rather than looking authoritative.
 */

import { Link } from 'react-router-dom';
import { useShortlist } from '../lib/shortlist-context.jsx';
import { IconCheck, IconLog, IconPlay, IconPlus, IconRegistry, IconSpinner } from './Icons.jsx';

/** "13 August 2026" - the same prose the API renders for a run's filters. */
function formatDate(iso) {
  if (!iso) return null;
  return new Date(iso).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

function formatCount(value) {
  return typeof value === 'number' ? value.toLocaleString() : '0';
}

/**
 * What the registry holds for one SIC code.
 *
 * Real from phase 5: the state comes from `sic_code_state` through the
 * `acquisitionStates` map every catalogue endpoint now returns. A code missing
 * from that map has never been acquired, which is why the prop is optional.
 *
 * The badge carries the *number*, not just the verdict. "Acquired" alone is the
 * kind of label that looks informative and answers nothing - 3 companies and
 * 173,000 companies are both "acquired" and mean entirely different things.
 */
export function AcquisitionBadge({ state, staleAfterDays = 90 }) {
  const kind = state?.state ?? 'never_acquired';
  const count = formatCount(state?.companiesTotal);
  const acquiredOn = formatDate(state?.lastAcquiredAt);

  const presentation = {
    never_acquired: {
      className: 'badge',
      label: 'Never acquired',
      tip: 'No acquisition run has been made for this code yet. Acquiring it fetches every company on the Companies House register that files it.',
    },
    acquiring: {
      className: 'badge badge-info',
      label: 'Acquiring',
      icon: <IconSpinner />,
      tip: 'A run is in progress for this code right now. The count will settle when it finishes.',
    },
    acquired: {
      className: 'badge badge-ok',
      label: `${count} ${state?.companiesTotal === 1 ? 'company' : 'companies'}`,
      tip: `Acquired in full${acquiredOn ? ` on ${acquiredOn}` : ''}. Every company Companies House holds under this code is in the registry.`,
    },
    partial: {
      className: 'badge badge-warn',
      label: `Partial · ${count}`,
      tip: `Partially acquired${acquiredOn ? `, last run ${acquiredOn}` : ''}. The run was cancelled, paused or left a coverage gap, so the registry holds ${count} companies for this code but not necessarily all of them. Re-acquiring resumes from what is already there - it does not duplicate anything.`,
    },
    failed: {
      className: 'badge badge-error',
      label: 'Failed',
      tip: 'The last run for this code ended in error and acquired nothing. Open the log to see why.',
    },
  }[kind] ?? { className: 'badge', label: kind, tip: null };

  return (
    <span className="badge-stack">
      <span className={`${presentation.className} tip tip-left`} data-tip={presentation.tip}>
        {presentation.icon ?? <span className="badge-dot" />}
        {presentation.label}
      </span>

      {/* Stale is a separate marker rather than a fifth state: the data is still
          held and still usable, so folding it into the verdict would overstate
          the problem. */}
      {state?.isStale ? (
        <span
          className="badge badge-warn tip tip-left"
          data-tip={`Last acquired ${state.daysSinceAcquired} days ago. Companies incorporate, dissolve and change their filed SIC codes continuously, so anything older than ${staleAfterDays} days is worth re-running. A re-acquisition updates in place - it never duplicates.`}
        >
          Stale
        </span>
      ) : null}
    </span>
  );
}

/**
 * Where a row goes once its code has been acquired.
 *
 * Hidden entirely for a code nothing has touched: "View log" on a code with no
 * runs opens an empty list, which is a worse answer than no button.
 */
function AcquisitionActions({ code, state }) {
  if (!state || state.state === 'never_acquired') return null;

  return (
    <span className="code-row-links">
      <Link
        className="btn btn-ghost btn-sm tip tip-left"
        to={`/log?sicCode=${code}`}
        data-tip="Every acquisition run for this code, with its filters, counts and the per-company trail."
      >
        <IconLog />
        View log
      </Link>

      {/* Live since phase 6. An ordinary link rather than cross-screen message
          passing, because the registry holds its filters in the URL. */}
      <Link
        className="btn btn-ghost btn-sm tip tip-left"
        to={`/registry?sicCode=${code}`}
        data-tip={`Open the ${formatCount(state.companiesTotal)} companies stored under this code in the registry, where they can be searched, filtered and read one by one.`}
      >
        <IconRegistry />
        View companies
      </Link>
    </span>
  );
}

/** Add / remove for one code, reflecting whatever the shared basket currently holds. */
export function ShortlistToggle({ code, source = 'manual', sourceDetail, size = 'sm' }) {
  const shortlist = useShortlist();
  const inList = shortlist.has(code);
  const busy = shortlist.isPending(code);

  const handleClick = () => {
    if (busy) return;
    if (inList) void shortlist.remove(code);
    else void shortlist.add(code, { source, sourceDetail });
  };

  return (
    <button
      type="button"
      className={`btn btn-${size} ${inList ? 'btn-ghost' : ''} tip tip-left`}
      onClick={handleClick}
      disabled={busy}
      data-tip={
        inList
          ? 'Remove this code from the shortlist'
          : 'Add this code to the shortlist. The shortlist survives a refresh and carries into the acquisition dialog in one action.'
      }
    >
      {busy ? <span className="spinner" /> : inList ? <IconCheck /> : <IconPlus />}
      {inList ? 'Shortlisted' : 'Shortlist'}
    </button>
  );
}

export default function SicCodeRow({
  entry,
  reason = null,
  activities = [],
  selected = false,
  onToggleSelect = null,
  shortlistSource = 'manual',
  shortlistDetail,
  // The entry from the endpoint's `acquisitionStates` map, or nothing at all
  // for a code that has never been acquired.
  acquisitionState = null,
  staleAfterDays = 90,
  // Opens the acquisition dialog for just this code. Omitted entirely on rows
  // that have nowhere to send it (there are none today, but nothing here
  // should assume every list will).
  onAcquire = null,
}) {
  const neverAcquired = !acquisitionState || acquisitionState.state === 'never_acquired';
  return (
    <div className={`code-row${selected ? ' is-selected' : ''}`}>
      {onToggleSelect ? (
        <input
          type="checkbox"
          className="checkbox"
          checked={selected}
          onChange={() => onToggleSelect(entry.code)}
          aria-label={`Select ${entry.code} ${entry.description}`}
        />
      ) : (
        <span />
      )}

      <span className="code-row-number">{entry.code}</span>

      <span className="code-row-body">
        <span className="code-row-description">{entry.description}</span>
        <span className="code-row-path">
          {entry.section} &middot; {entry.sectionName} &rarr; {entry.division}{' '}
          {entry.divisionName}
        </span>

        {reason?.detail && reason.kind !== 'description' ? (
          <span className="code-row-reason">
            {reason.label}: <em>{reason.detail}</em>
          </span>
        ) : null}

        {/* Everyday names for this code, shown when the official wording is not
            what the user typed. "Other activities of employment placement
            agencies" means nothing without them. */}
        {activities.length > 0 && reason?.kind !== 'activity' ? (
          <span className="code-row-reason text-faint">
            also called: {activities.slice(0, 3).join(' · ')}
          </span>
        ) : null}
      </span>

      {/* One grid column, two rows: the row stays a four-column grid, and a
          code with nowhere to go looks exactly as it did before. */}
      <span className="code-row-actions">
        <span className="code-row-actions-main">
          {onAcquire && neverAcquired ? (
            <button
              type="button"
              className="btn btn-primary btn-sm tip tip-left"
              onClick={() => onAcquire(entry.code)}
              data-tip="Fetch every company Companies House holds under this code into the registry. You will see the real count and cost before anything starts."
            >
              <IconPlay />
              Acquire
            </button>
          ) : null}
          <AcquisitionBadge state={acquisitionState} staleAfterDays={staleAfterDays} />
          <ShortlistToggle
            code={entry.code}
            source={shortlistSource}
            sourceDetail={shortlistDetail}
          />
        </span>

        <AcquisitionActions code={entry.code} state={acquisitionState} />
      </span>
    </div>
  );
}
